import m from "mithril";
import { getUserData, getMithril, postMithril, putMithril, deleteMithril } from "../utils.js";
import toast, { showConfirmation } from "../js/toaster.js";

var Officers = {
    officers: [],
    loading: true,
    error: null,
    showForm: false,
    editingOfficer: null,
    isLoading: false,
    // User data state for async loading
    userData: null,
    userDataLoading: false,
    formData: {
        code: '',
        name: '',
        chair: '',
        username: '',
        password: '',
        email: '',
        telephone: ''
    },

    // Load user data with proper async handling (following Manajer.js pattern)
    loadUserData: async function () {
        try {
            console.log('🔍 OFFICERS DEBUG: Loading user data...');
            this.userDataLoading = true;
            m.redraw();

            const userData = await getUserData(true); // Force refresh to get current session
            console.log('🔍 OFFICERS DEBUG: Fresh user data:', userData ? 'Promise resolved' : 'null');

            this.userData = userData;
            this.userDataLoading = false;
            console.log('🔍 OFFICERS DEBUG: User data loaded successfully');
            m.redraw();

            return userData;
        } catch (error) {
            console.error('🔍 OFFICERS DEBUG: Error loading user data:', error);
            this.userDataLoading = false;
            this.error = 'Failed to load user data: ' + error.message;
            m.redraw();
            return null;
        }
    },

    async oninit(vnode) {
        try {
            console.log('🔍 OFFICERS DEBUG: Component initializing...');

            // Load user data first with retry mechanism
            await this.loadUserDataWithRetry();
            await this.loadOfficers();

            console.log('🔍 OFFICERS DEBUG: Component initialized successfully');
        } catch (error) {
            console.error('🔍 OFFICERS DEBUG: Error during initialization:', error);
            this.error = 'Failed to initialize component: ' + error.message;
            toast.error(this.error);
        }
    },

    // Enhanced user data loading with retry mechanism
    loadUserDataWithRetry: async function (attempts = 3) {
        for (let i = 0; i < attempts; i++) {
            try {
                await this.loadUserData();
                if (this.userData) {
                    console.log('🔍 OFFICERS DEBUG: User data loaded successfully after', i + 1, 'attempts');

                    // Check if user is manajer or superuser
                    if (this.userData.role !== 'manajer' && this.userData.role !== 'superuser') {
                        console.log('🔍 OFFICERS DEBUG: Access denied for role:', this.userData.role);
                        m.route.set('/unauthorized');
                        return;
                    }

                    console.log('🔍 OFFICERS DEBUG: Access granted for role:', this.userData.role);
                    return;
                }
                console.log('🔍 OFFICERS DEBUG: User data still null, retrying...', i + 1);
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
            } catch (error) {
                console.error('🔍 OFFICERS DEBUG: Error loading user data, attempt', i + 1, ':', error);
                if (i === attempts - 1) {
                    throw error;
                }
            }
        }
    },

    loadOfficers: async function () {
        this.loading = true;
        this.error = null;

        try {
            const result = await getMithril('/api/officers');
            if (result.success) {
                this.officers = result.data;
                if (this.officers.length > 0) {
                    toast.success('Data pejabat berhasil dimuat');
                }
            } else {
                this.error = result.error;
                toast.error('Gagal memuat data pejabat: ' + result.error);
            }
        } catch (error) {
            this.error = error.message;
            toast.error('Gagal memuat data pejabat: ' + error.message);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    saveOfficer: async function () {
        this.isLoading = true;
        m.redraw();

        const data = { ...this.formData };
        if (this.editingOfficer) {
            data.id = this.editingOfficer.id;
            // Don't send password if it's empty during edit (for security)
            if (!data.password || data.password.trim() === '') {
                delete data.password;
            }
        }

        try {
            const result = this.editingOfficer
                ? await putMithril('/api/officers', data)
                : await postMithril('/api/officers', data);

            if (result.success) {
                this.loadOfficers();
                this.resetForm();
                toast.success(this.editingOfficer ? 'Pejabat berhasil diupdate' : 'Pejabat berhasil dibuat');
            } else {
                toast.error('Gagal menyimpan pejabat: ' + result.error);
            }
        } catch (error) {
            toast.error('Gagal menyimpan pejabat: ' + error.message);
        } finally {
            this.isLoading = false;
            m.redraw();
        }
    },

    deleteOfficer: function (officer) {
        showConfirmation(
            'Apakah Anda yakin ingin menghapus pejabat ' + officer.name + '?',
            async () => {
                this.isLoading = true;
                m.redraw();

                try {
                    const response = await deleteMithril('/api/officers?id=' + officer.id);

                    if (response.success) {
                        this.loadOfficers();
                        toast.success('Pejabat berhasil dihapus');
                    } else {
                        toast.error('Gagal menghapus pejabat: ' + response.error);
                    }
                } catch (error) {
                    toast.error('Gagal menghapus pejabat: ' + error.message);
                } finally {
                    this.isLoading = false;
                    m.redraw();
                }
            },
            () => {
                toast.info('Penghapusan dibatalkan');
            }
        );
    },

    editOfficer: function (officer) {
        this.editingOfficer = officer;
        this.formData = {
            code: officer.code,
            name: officer.name,
            chair: officer.chair,
            username: officer.username || '',
            password: '', // Don't populate password for security
            email: officer.email || '',
            telephone: officer.telephone || ''
        };
        this.showForm = true;
    },

    resetForm: function () {
        this.editingOfficer = null;
        this.formData = {
            code: '',
            name: '',
            chair: '',
            username: '',
            password: '',
            email: '',
            telephone: ''
        };
        this.showForm = false;
        this.isLoading = false;
    },

    view: function (vnode) {
        // Show loading spinner while initial user data is loading
        if (this.userDataLoading) {
            return m("div", { class: "container mx-auto p-4" }, [
                m("div", { class: "flex justify-center items-center h-64" }, [
                    m("div", { class: "text-center" }, [
                        m("i", { class: "ri-loader-4-line animate-spin text-4xl text-orange-600 mb-4" }),
                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Loading officers data...")
                    ])
                ])
            ]);
        }

        // Show error state if there's an error and no data
        if (this.error && this.officers.length === 0) {
            return m("div", { class: "container mx-auto p-4" }, [
                m("div", { class: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" }, [
                    m("p", { class: "font-bold" }, "Error Loading Data"),
                    m("p", this.error),
                    m("div", { class: "mt-2" }, [
                        m("button", {
                            class: "bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded mr-2",
                            onclick: () => this.loadOfficers()
                        }, "Retry"),
                        m("button", {
                            class: "bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded",
                            onclick: () => window.location.href = '/login'
                        }, "Login Again")
                    ])
                ])
            ]);
        }

        return [
            m("div", { class: "container mx-auto p-4" }, [
                m("div", { class: "flex justify-between items-center mb-4" }, [
                    m("h1", { class: "text-2xl font-bold" }, "Data Pejabat"),
                    m("button", {
                        class: "bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded",
                        onclick: () => this.showForm = !this.showForm
                    }, this.showForm ? "Batal" : "+ Pejabat")
                ]),

                // Error message
                this.error ? m("div", { class: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" }, this.error) : null,

                // Form
                this.showForm ? m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6" }, [
                    m("h2", { class: "text-xl font-bold mb-4" }, this.editingOfficer ? "Edit Pejabat" : "Tambah Pejabat Baru"),
                    m("form", {
                        onsubmit: (e) => {
                            e.preventDefault();
                            this.saveOfficer();
                        }
                    }, [
                        m("div", { class: "mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded" }, [
                            m("p", { class: "text-sm text-blue-700 dark:text-blue-300" }, [
                                m("strong", "Catatan:"), " Username dan Password wajib diisi untuk membuat akun login pejabat. Password akan di-hash sebelum disimpan ke database."
                            ])
                        ]),
                        m("div", { class: "grid grid-cols-2 gap-4" }, [
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Kode *"),
                                m("input", {
                                    type: "text",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.code,
                                    oninput: (e) => this.formData.code = e.target.value,
                                    required: true
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Nama *"),
                                m("input", {
                                    type: "text",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.name,
                                    oninput: (e) => this.formData.name = e.target.value,
                                    required: true
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Jabatan/Bagian*"),
                                m("select", {
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.chair,
                                    oninput: (e) => this.formData.chair = e.target.value,
                                    required: true
                                }, [
                                    m("option", { value: "" }, "Pilih Jabatan"),
                                    m("option", { value: "Koordinator" }, "Koordinator"),
                                    m("option", { value: "Kasir" }, "Kasir"),
                                    m("option", { value: "Verifikator" }, "Verifikator"),
                                    m("option", { value: "Akuntansi" }, "Akuntansi"),
                                    m("option", { value: "Bagian Kredit" }, "Bagian Kredit")
                                ])
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Username *"),
                                m("input", {
                                    type: "text",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.username,
                                    oninput: (e) => this.formData.username = e.target.value,
                                    required: true
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Password *"),
                                m("input", {
                                    type: "text",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.password,
                                    oninput: (e) => this.formData.password = e.target.value,
                                    required: true
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Email *"),
                                m("input", {
                                    type: "email",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.email,
                                    oninput: (e) => this.formData.email = e.target.value,
                                    required: true
                                })
                            ]),
                            m("div", [
                                m("label", { class: "block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2" }, "Telepon *"),
                                m("input", {
                                    type: "tel",
                                    class: "shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 leading-tight focus:outline-none focus:shadow-outline",
                                    value: this.formData.telephone,
                                    oninput: (e) => this.formData.telephone = e.target.value,
                                    placeholder: "Masukkan nomor telepon",
                                    required: true
                                })
                            ])
                        ]),
                        m("div", { class: "flex items-center justify-between mt-4" }, [
                            m("button", {
                                type: "submit",
                                class: "bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline",
                                disabled: this.isLoading
                            }, this.isLoading ? [
                                m("i", { class: "ri-loader-4-line animate-spin mr-2" }),
                                "Menyimpan..."
                            ] : (this.editingOfficer ? "Update Pejabat" : "Buat Pejabat")),
                            m("button", {
                                type: "button",
                                class: "bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ml-2",
                                onclick: () => this.resetForm(),
                                disabled: this.isLoading
                            }, "Batal")
                        ])
                    ])
                ]) : null,

                // Officers list
                m("div", { class: "bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Kode"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Nama"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Jabatan"),
                                m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" }, "Aksi")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            // Show loading spinner in table when loading
                            this.loading && m("tr", [
                                m("td", { class: "px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "4" }, [
                                    m("div", { class: "flex justify-center items-center my-8" }, [
                                        m("i", { class: "ri-loader-4-line animate-spin text-2xl text-orange-600" })
                                    ])
                                ])
                            ]),
                            // Show empty state when no officers and not loading
                            !this.loading && this.officers.length === 0 && m("tr", [
                                m("td", { class: "px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500", colspan: "4" }, "Belum ada data Pejabat")
                            ]),
                            // Show officers data when not loading
                            !this.loading && this.officers.map((officer) =>
                                m("tr", { key: officer.id }, [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, officer.code),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, officer.name),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, officer.chair),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("button", {
                                            class: "text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 mr-3",
                                            onclick: () => this.editOfficer(officer),
                                            title: "Edit pejabat"
                                        }, [
                                            m("i", { class: "ri-edit-line" })
                                        ]),
                                        m("button", {
                                            class: "text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300",
                                            onclick: () => this.deleteOfficer(officer),
                                            title: "Hapus pejabat"
                                        }, [
                                            m("i", { class: "ri-delete-bin-line" })
                                        ])
                                    ])
                                ])
                            )
                        ])
                    ])
                ])
            ])
        ];
    }
};

export default Officers;